import { useCallback, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { PaginationState } from '@tanstack/react-table';
import { DateTime } from 'luxon';

import { useAppSelector } from 'src/app/store/hooks';
import { DataGrid } from 'src/common/DataGrid';
import { useExpandableTable } from 'src/common/DataGrid/useExpandableTable';
import { usePagination } from 'src/common/DataGrid/usePagination';
import {
  DaysOfWeekEnum,
  RouteTemplateRead,
  useRoutesRouteTemplatesListQuery,
} from 'src/common/external/bambi-api/routesApi';
import { EmptyState } from 'src/common/primitives/EmptyState/EmptyState';

import { RouteTemplateIcon } from '../components/RouteTemplateIcon';
import { useEditRouteTemplateModal } from '../hooks/useEditRouteTemplateModal';
import { columns as configuredColumns } from './columns';
import { orderDays } from './DayOfTheWeekCell';
import { ExpandedRouteTemplateRow } from './ExpandedRouteTemplateRow';

export function RouteTemplateDataGrid() {
  const [currentPage, setCurrentPage] = useState(1);
  const { expanded, setExpanded, columns } =
    useExpandableTable<RouteTemplateRead>(configuredColumns, 70);

  const search = useAppSelector((state) => state.routeTemplates.filterSearch);
  const defaultDriverIn = useAppSelector(
    (state) => state.routeTemplates.filterDriver
  );
  const daysOfWeek = useAppSelector(
    (state) => state.routeTemplates.filterDaysOfWeek
  );
  const filterRangeStart = useAppSelector(
    (state) => state.routeTemplates.filterRangeStart
  );
  const filterRangeEnd = useAppSelector(
    (state) => state.routeTemplates.filterRangeEnd
  );

  const { data, isLoading, refetch } = useRoutesRouteTemplatesListQuery({
    page: currentPage,
    name: search,
    defaultDriverIn,
    daysOfWeek: orderDays(daysOfWeek as DaysOfWeekEnum[]).join(','),
    activeAfter: filterRangeStart
      ? DateTime.fromISO(filterRangeStart).toSQLDate()
      : undefined,
    activeBefore: filterRangeEnd
      ? DateTime.fromISO(filterRangeEnd).toSQLDate()
      : undefined,
  });

  const { editRouteTemplateModal } = useEditRouteTemplateModal();
  const handleClick = useCallback(() => {
    editRouteTemplateModal(undefined);
  }, [editRouteTemplateModal]);

  const handlePaginationUpdate = useCallback(
    (state: PaginationState) => {
      setCurrentPage(state.pageIndex + 1);
      refetch();
    },
    [refetch]
  );

  const { pagination, handlePaginationChange } = usePagination(
    handlePaginationUpdate
  );

  if (isLoading) {
    return (
      <div className="flex flex-row items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <DataGrid
      onExpandedChange={setExpanded}
      expandedState={expanded}
      rowExpanderMode="row"
      expandedRowRenderer={(row) => (
        <ExpandedRouteTemplateRow routeTemplateId={row.original.id} />
      )}
      getRowId={(row) => row.id}
      tableId="routeTemplateDataGrid"
      columns={columns}
      data={data?.results ?? []}
      pagination={pagination}
      handlePaginationChange={handlePaginationChange}
      totalCount={data?.count ?? 0}
      emptyState={
        <EmptyState
          Icon={<RouteTemplateIcon />}
          entityName="Route Template"
          description="No templates found, create a reusable route template to get started."
          Action={
            <Button variant="primary" onClick={handleClick}>
              Add Route Template
            </Button>
          }
        />
      }
    />
  );
}
